// src/pages/JewelleryPlans/Redemptions/PlanQuotes.jsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllPlansThunk } from "../../../store/thunks/jewelleryPlan/planThunks";
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  ScaleIcon,
  CurrencyRupeeIcon,
  LockClosedIcon,
} from "@heroicons/react/outline";

const PlanQuotes = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { quotes, selectedPlan } = useSelector(
    (state) => state.jewelleryPlan?.plans
  );
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    fetchQuotes();
  }, [planId, filters.page]);

  const fetchQuotes = async () => {
    await dispatch(getAllPlansThunk({ planId, filters }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const getQuoteStatus = (quote) => {
    if (quote.usedForRedemption) {
      return { color: "green", icon: CheckCircleIcon, text: "Used" };
    }
    if (quote.isExpired || new Date(quote.expiresAt) < new Date()) {
      return { color: "red", icon: XCircleIcon, text: "Expired" };
    }
    return { color: "yellow", icon: ClockIcon, text: "Valid" };
  };

  // Get plan type display name
  const getPlanTypeDisplay = (type) => {
    switch (type) {
      case "VALUE_SAVINGS":
        return "Fixed Value";
      case "WEIGHT_SAVINGS":
        return "Gold Weight";
      case "PRICE_LOCK":
        return "Price Lock";
      default:
        return type || "Unknown";
    }
  };

  // Get the main value to display based on plan type
  const getQuoteValue = (quote) => {
    const planType = quote.planType;

    if (planType === "VALUE_SAVINGS") {
      return {
        primary: formatAmount(quote.breakdown?.valueInr || 0),
        secondary: quote.breakdown?.benefitWaivePercent
          ? `${quote.breakdown.benefitWaivePercent}% waiver`
          : null,
        icon: CurrencyRupeeIcon,
      };
    } else if (planType === "WEIGHT_SAVINGS" || planType === "PRICE_LOCK") {
      return {
        primary: `${quote.breakdown?.totalGrams || 0}g`,
        secondary: formatAmount(quote.breakdown?.lockedValue || 0),
        icon: planType === "PRICE_LOCK" ? LockClosedIcon : ScaleIcon,
      };
    }

    // Fallback for unknown plan types
    return {
      primary: "N/A",
      secondary: null,
      icon: DocumentTextIcon,
    };
  };

  // Get rate strategy badge
  const getRateStrategyBadge = (strategy) => {
    const strategies = {
      CURRENT_WITH_BENEFITS: { text: "Current + Benefits", color: "green" },
      CURRENT_NO_BENEFITS: { text: "Current Rate", color: "yellow" },
      MATURITY_LOCKED: { text: "Maturity Rate", color: "red" },
      FIXED_VALUE: { text: "Fixed Value", color: "blue" },
      PRICE_LOCKED: { text: "Price Locked", color: "purple" },
    };
    return (
      strategies[strategy] || { text: strategy || "Unknown", color: "gray" }
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate(`/jewellery/${planId}`)}
            className="text-gray-500 hover:text-gray-700"
          >
            ← Back to Plan
          </button>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Quotes for Plan #{selectedPlan?.planNumber || planId}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View all redemption quotes created for this plan
            </p>
          </div>
          <button
            onClick={() => navigate(`/jewellery/${planId}/create-quote`)}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create New Quote
          </button>
        </div>
      </div>

      {/* Quotes List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quote Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value / Grams
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate Strategy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotes.loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    </div>
                  </td>
                </tr>
              ) : quotes.list?.length > 0 ? (
                quotes.list.map((quote) => {
                  const status = getQuoteStatus(quote);
                  const StatusIcon = status.icon;
                  const quoteValue = getQuoteValue(quote);
                  const ValueIcon = quoteValue.icon;
                  const rateStrategy = getRateStrategyBadge(
                    quote.rateSnapshot?.strategy
                  );

                  return (
                    <tr key={quote._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {quote.quoteNumber}
                        </div>
                        {quote.selectedKarat && (
                          <div className="text-xs text-gray-500">
                            {quote.selectedKarat}K Gold
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(quote.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getPlanTypeDisplay(quote.planType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ValueIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {quoteValue.primary}
                            </div>
                            {quoteValue.secondary && (
                              <div className="text-xs text-gray-500">
                                {quoteValue.secondary}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${rateStrategy.color}-100 text-${rateStrategy.color}-800`}
                        >
                          {rateStrategy.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(quote.expiresAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}
                        >
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.text}
                        </span>
                        {quote.usedForRedemption && quote.redemptionId && (
                          <button
                            onClick={() =>
                              navigate(`/redemptions/${quote.redemptionId}`)
                            }
                            className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                          >
                            View Redemption
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() =>
                            navigate(`/jewellery/${planId}/quote/${quote._id}`)
                          }
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    <div className="py-8">
                      <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">
                        No quotes created yet
                      </p>
                      <button
                        onClick={() =>
                          navigate(`/jewellery/${planId}/create-quote`)
                        }
                        className="text-yellow-600 hover:text-yellow-700 font-medium"
                      >
                        Create your first quote →
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {quotes.list?.length > 0 && quotes.pagination && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                disabled={filters.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                disabled={filters.page >= quotes.pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(filters.page - 1) * filters.limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      filters.page * filters.limit,
                      quotes.pagination.total
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">{quotes.pagination.total}</span>{" "}
                  quotes
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() =>
                      setFilters((f) => ({ ...f, page: f.page - 1 }))
                    }
                    disabled={filters.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {filters.page} of {quotes.pagination.totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setFilters((f) => ({ ...f, page: f.page + 1 }))
                    }
                    disabled={filters.page >= quotes.pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanQuotes;
